import i18n from '@/utils/i18n';
import {
  bookmarkKey,
  usePricingBookmarksStore,
} from '@sales-portal/domain/pricing/blocs/stores/pricing-bookmarks-store';
import { buildAccountKey } from '@sales-portal/domain/pricing/blocs/utils/account-key';
import {
  DeletePricingBookmarkEntryInput,
  GetPricingBookmarksQuery,
  UpsertPricingBookmarkEntryInput,
} from '@/types';
import { GetPricingBookmarksDocument } from '@sales-portal/domain/pricing/blocs/documents/get-pricing-bookmarks.generated';
import { useCallback } from 'react';
import { useDeletePricingBookmarksMutation } from '@sales-portal/domain/pricing/blocs/documents/delete-pricing-bookmarks.generated';
import { useExperienceToasts } from '@experiences/hooks/use-experience-toasts';
import { useUpsertPricingBookmarksMutation } from '@sales-portal/domain/pricing/blocs/documents/upsert-pricing-bookmarks.generated';

const pairKey = (variantPackUUID = '', restaurantUUID = '') =>
  buildAccountKey({ restaurantUUID, variantPackUUID });

export const useBookmarkMutator = () => {
  const { showErrorToast } = useExperienceToasts();
  const setBookmark = usePricingBookmarksStore((store) => store.setBookmark);
  const removeBookmark = usePricingBookmarksStore(
    (store) => store.removeBookmark,
  );
  const [upsertMutation, { loading: upsertLoading }] =
    useUpsertPricingBookmarksMutation();
  const [deleteMutation, { loading: deleteLoading }] =
    useDeletePricingBookmarksMutation();

  // Snapshot each pair's current store entry (before the optimistic write) so a
  // failed mutation can roll the store back to exactly where it was.
  const snapshotRollback = useCallback(
    (pairs: { restaurant_id: string; variant_pack_id: string }[]) => {
      const { bookmarks } = usePricingBookmarksStore.getState();
      const prior = pairs.map(({ restaurant_id, variant_pack_id }) => ({
        entry: bookmarks[bookmarkKey(variant_pack_id, restaurant_id)] ?? null,
        restaurant_id,
        variant_pack_id,
      }));
      return () =>
        prior.forEach(({ entry, restaurant_id, variant_pack_id }) => {
          if (entry) {
            // Pass the captured bookmarkedAt: the entry is already cleared from
            // the store, so setBookmark would otherwise stamp the current time.
            setBookmark(
              variant_pack_id,
              restaurant_id,
              entry.note,
              entry.bookmarkedAt,
            );
          } else {
            removeBookmark(variant_pack_id, restaurant_id);
          }
        });
    },
    [removeBookmark, setBookmark],
  );

  // Optimistically flip the store (instant icon everywhere), then persist. No
  // read refetch: the store drives every row icon and the sidebar count, and
  // the Bookmarked tab — the only consumer of the enriched rows — refetches on
  // focus, which is the only way to reach it after bookmarking elsewhere.
  const bookmark = useCallback(
    async (entries: UpsertPricingBookmarkEntryInput[]) => {
      if (entries.length === 0) {
        return;
      }
      entries.forEach((entry) =>
        setBookmark(
          entry.variant_pack_id,
          entry.restaurant_id,
          entry.note ?? null,
        ),
      );
      try {
        return await upsertMutation({ variables: { entries } });
      } catch {
        // The `[B]` Bookmark action is gated to not-yet-bookmarked pairs, so a
        // bookmark is always a create — a failed one reverts to unbookmarked.
        // Snapshot-restore would be wrong here: the UI flips the store on `[B]`
        // before this runs, so the "prior" state is already bookmarked. (A
        // future edit-existing-note flow would need snapshot-based rollback.)
        entries.forEach((entry) =>
          removeBookmark(entry.variant_pack_id, entry.restaurant_id),
        );
        showErrorToast(i18n.t('pricing.bookmarks.save_error'));
      }
    },
    [removeBookmark, setBookmark, showErrorToast, upsertMutation],
  );

  // Unbookmark has the pair ids in hand, so evict the matching rows from the
  // read cache directly instead of re-running the expensive fan-out.
  const unbookmark = useCallback(
    async (entries: DeletePricingBookmarkEntryInput[]) => {
      if (entries.length === 0) {
        return;
      }
      const rollback = snapshotRollback(entries);
      entries.forEach((entry) =>
        removeBookmark(entry.variant_pack_id, entry.restaurant_id),
      );
      const removed = new Set(
        entries.map((entry) =>
          pairKey(entry.variant_pack_id, entry.restaurant_id),
        ),
      );
      try {
        return await deleteMutation({
          update: (cache) => {
            const existing = cache.readQuery<GetPricingBookmarksQuery>({
              query: GetPricingBookmarksDocument,
            });
            if (!existing) {
              return;
            }
            cache.writeQuery<GetPricingBookmarksQuery>({
              data: {
                getPricingBookmarks: {
                  ...existing.getPricingBookmarks,
                  bookmarks: existing.getPricingBookmarks.bookmarks.filter(
                    (row) =>
                      !removed.has(
                        pairKey(row.variant_pack?.uuid, row.restaurant?.uuid),
                      ),
                  ),
                },
              },
              query: GetPricingBookmarksDocument,
            });
          },
          variables: { entries },
        });
      } catch {
        rollback();
        showErrorToast(i18n.t('pricing.bookmarks.remove_error'));
      }
    },
    [deleteMutation, removeBookmark, showErrorToast, snapshotRollback],
  );

  return {
    bookmark,
    loading: upsertLoading || deleteLoading,
    unbookmark,
  };
};
